import { Contact, ContactForm, Product, ProductForm } from './interfaces/generalInterfaces';
import axios from 'axios';
import FileDownload from 'js-file-download';
import { myFirebaseAuth, myFirebaseStorage } from '../firebase/config';
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import JSZip from 'jszip';
import {
  FromFieldTypeNoticeTemplate,
  FromFieldTypeReleaseTemplate,
  LienTemplate,
  NoticeTemplate,
  PDFFieldReleaseTemplate,
  Supplier,
  SupplierIntegrationType,
  formatMoney,
  promiseNull,
  validateEmail,
  validatePhoneNumber,
} from '@dill/dill-shared';
import { PDFDocument, setFontAndSize, PDFName, PDFRef, StandardFonts } from 'pdf-lib';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { REACT_APP_FRONTEND_URL } from './constants';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';

export const createFormFromContact = (contact: Contact): ContactForm => {
  const newContactForm: ContactForm = {
    companyId: { id: 'companyId', name: '', value: contact.companyId, errorText: '' },
    firstName: { id: 'firstName', name: '', value: contact.firstName, errorText: '' },
    lastName: { id: 'lastName', name: '', value: contact.lastName, errorText: '' },
    restaurant: { id: 'restaurant', name: '', value: contact.restaurant, errorText: '' },
    phoneNumber: { id: 'phoneNumber', name: '', value: contact.phoneNumber, errorText: '' },
    emailAddress: { id: 'email', name: '', value: contact.emailAddress, errorText: '' },
    priceListId: {
      id: 'priceListId',
      name: '',
      value: contact.priceListId,
      // value: { value: contact.priceListId, label: contact.priceListId },
      errorText: '',
      options: [],
    },
    isSharePersonalCell: {
      id: 'isSharePersonalCell',
      name: '',
      value: contact.isSharePersonalCell,
      errorText: '',
    },
  };
  return newContactForm;
};
export const createFormFromProduct = (product: Product): ProductForm => {
  const newProductForm: ProductForm = {
    productName: { id: 'productName', name: '', value: product.productName, errorText: '' },
    category: { id: 'category', name: '', value: product.category, errorText: '' },
    price: { id: 'price', name: '', value: product.price, errorText: '' },
    unit: { id: 'unit', name: '', value: product.unit, errorText: '' },
  };
  return newProductForm;
};

export const validateContact = (contact: Contact) => {
  let isError = false;
  const contactForm = createFormFromContact(contact);
  if (!contact.firstName) {
    isError = true;
    contactForm.firstName.errorText = 'This field is required';
  } else {
    contactForm.firstName.errorText = '';
  }
  if (contact.phoneNumber && !validatePhoneNumber(contact.phoneNumber)) {
    isError = true;
    contactForm.phoneNumber.errorText = 'Please input a valid phone number';
  } else {
    contactForm.phoneNumber.errorText = '';
  }
  if (contact.emailAddress && !validateEmail(contact.emailAddress)) {
    isError = true;
    contactForm.emailAddress.errorText = 'Please input a valid email';
  } else {
    contactForm.emailAddress.errorText = '';
  }
  return { contactForm, isError };
};

export const validateProduct = (product: Product) => {
  let isError = false;
  const productForm = createFormFromProduct(product);
  if (!product.productName) {
    isError = true;
    productForm.productName.errorText = 'This field is required';
  } else {
    productForm.productName.errorText = '';
  }
  if (!product.price) {
    isError = true;
    productForm.price.errorText = 'This field is required';
  } else {
    productForm.price.errorText = '';
  }

  return { productForm, isError };
};

export const convertHexToRGBA = (hexCode: string, opacity: number) => {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

export const DLog = (message: any, obj = null) => {
  if (process.env.REACT_APP_ENVIRONMENT === 'production') return;
  console.log(message, obj !== null ? obj : '');
};

/**
 * Whether a payment may be scheduled for today.
 *
 * In production the earliest is tomorrow: today is indistinguishable from paying now, and it races
 * the 9am run that may already have gone. Outside production today is allowed so the feature can be
 * exercised end to end in one run instead of waiting a day. The backend enforces the same rule, so
 * this only keeps the picker and the client-side message in step with it.
 */
export const isSameDaySchedulingAllowed = (): boolean =>
  process.env.REACT_APP_ENVIRONMENT !== 'production';

/**
 * "Today" as the backend sees it.
 *
 * The scheduling rule is a calendar-day comparison in America/Los_Angeles — the timezone the run
 * cron uses — so the picker has to ask the same question. Using the browser's local day instead
 * breaks for anyone west of PT: at 10pm in Honolulu it is already tomorrow in PT, so the picker
 * offers a date the backend then rejects as not in the future.
 */
const SCHEDULING_TIMEZONE = 'America/Los_Angeles';
const todayInSchedulingTimezone = () => momentTimezone().tz(SCHEDULING_TIMEZONE).startOf('day');

/**
 * The earliest date the schedule picker should offer, honouring the rule above.
 *
 * Returned as a local-midnight Date because that is what the picker and `moment(date).format` on
 * the way out both work in: the calendar day is what matters, and it must survive the round trip
 * unchanged.
 */
export const earliestSchedulableDate = (): Date => {
  const earliest = todayInSchedulingTimezone();
  if (!isSameDaySchedulingAllowed()) {
    earliest.add(1, 'day');
  }
  return moment(earliest.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').toDate();
};

/** True when `date` is too early to schedule, matching the backend's validation. */
export const isBeforeEarliestSchedulableDate = (date: Date): boolean =>
  moment(date).startOf('day').isBefore(moment(earliestSchedulableDate()).startOf('day'));

/** Message shown when the chosen date is too early. */
export const earliestSchedulableDateMessage = (): string =>
  isSameDaySchedulingAllowed()
    ? 'Pick today or a future date'
    : 'Pick a date in the future — the earliest you can schedule is tomorrow';

export const getProductFromForm = (productForm: ProductForm): Product => {
  const product: Product = {
    productName: productForm.productName.value,
    category: productForm.category.value,
    price: productForm.price.value,
    unit: productForm.unit.value,
    isSaved: false,
  };
  return product;
};

export const getContactFromForm = (contactForm: ContactForm): Contact => {
  const contact: Contact = {
    companyId: contactForm.companyId.value,
    firstName: contactForm.firstName.value,
    lastName: contactForm.lastName.value,
    emailAddress: contactForm.emailAddress.value,
    phoneNumber: contactForm.phoneNumber.value,
    restaurant: contactForm.restaurant.value,
    isSharePersonalCell: contactForm.isSharePersonalCell.value,
    priceListId: contactForm.priceListId.value,
    // previewPriceListLink: contactForm.previewPriceListLink.value,
    isSaved: false,
  };
  return contact;
};

export const downloadFile = (url: string, fileName: string) => {
  axios({
    url,
    method: 'GET',
    responseType: 'blob', // Important
  }).then((response) => {
    FileDownload(response.data, fileName);
  });
};

export const printFile = (url: string, fileName: string) => {
  axios({
    url,
    method: 'GET',
    responseType: 'blob', // Important
  }).then((response) => {
    const file = new Blob([response.data], { type: 'application/pdf' });

    // Create an invisible iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = window.URL.createObjectURL(file);

    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: Remove the iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  });
};

export const firebaseFileUploadHelper = async ({
  blobUrl,
  storagePath,
  fileNameWithExtension,
}: {
  blobUrl: string;
  storagePath: string;
  fileNameWithExtension: string;
}): Promise<string | null> => {
  const templateRef = ref(myFirebaseStorage, `${storagePath}/${fileNameWithExtension}`);
  const pdfBlob = await axios({
    method: 'get',
    url: blobUrl,
    responseType: 'blob',
  });
  if (pdfBlob.data) {
    const uploadResults = await uploadBytes(templateRef, pdfBlob.data);
    // console.log(uploadResults);

    const downloadURL = await getDownloadURL(uploadResults.ref);
    return downloadURL;
  }
  return null;
};

export const firebaseLargeFileUploadHelper = async ({
  blobUrl,
  storagePath,
  fileNameWithExtension,
  onProgress, // Optional progress callback
  maxAttempts = 3,
}: {
  blobUrl: string;
  storagePath: string;
  fileNameWithExtension: string;
  onProgress?: (progress: number) => void;
  maxAttempts?: number;
}): Promise<string | null> => {
  const attemptUpload = async (): Promise<string> => {
    // Create a storage reference
    const templateRef = ref(myFirebaseStorage, `${storagePath}/${fileNameWithExtension}`);

    // Fetch the file as a blob
    const pdfBlob = await axios.get(blobUrl, { responseType: 'blob' });

    if (!pdfBlob.data) {
      throw new Error(`No data returned reading blob for "${fileNameWithExtension}".`);
    }

    // Start resumable upload
    const uploadTask = uploadBytesResumable(templateRef, pdfBlob.data);

    return new Promise<string>((resolve, reject) => {
      // Listen for state changes
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Optional: Track progress
          if (onProgress) {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          }
        },
        (error) => {
          // Handle errors
          reject(error);
        },
        async () => {
          // Upload complete, get download URL
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await attemptUpload();
    } catch (error) {
      lastError = error;
      // Blob reads and resumable-upload state changes are both known to fail
      // transiently (dropped connection, momentary memory pressure from other
      // concurrent uploads, etc.) — back off briefly and retry before giving up.
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 500));
      }
    }
  }

  console.error(`File upload failed after ${maxAttempts} attempt(s):`, lastError);
  return null;
};

/**
 * Runs `mapper` over `items` with at most `limit` calls in flight at once,
 * preserving result order (like `Promise.all`, but bounded). Use this instead
 * of a bare `Promise.all(items.map(...))` when the work is resource-heavy
 * (e.g. file uploads) and unbounded concurrency risks contention/failures.
 */
export async function mapWithConcurrencyLimit<T, R>(
  items: T[],
  limit: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  });

  await Promise.all(workers);
  return results;
}

export async function downloadPDFsAsZip(
  pdfFiles: { url: string; name: string }[],
  zipFileName: string
): Promise<void> {
  const zip = new JSZip();

  const downloadPromises = pdfFiles.map(async (file, index) => {
    try {
      const response = await fetch(file.url);
      const pdfData = await response.arrayBuffer();
      const fileName = `${file.name}.pdf`;
      zip.file(fileName, pdfData);
    } catch (error) {
      console.error(`Failed to download ${file.url}: ${error}`);
    }
  });

  await Promise.all(downloadPromises);

  const zipContent = await zip.generateAsync({ type: 'blob' });

  const anchorElement = document.createElement('a');
  anchorElement.href = URL.createObjectURL(zipContent);
  anchorElement.download = zipFileName;
  anchorElement.click();
  URL.revokeObjectURL(anchorElement.href);

  console.log(`Zip file "${zipFileName}" created and downloaded successfully.`);
}

export const isEmailValid = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const extractEmailAddresses = (inputString: string, unique?: boolean): string[] => {
  // Define a regular expression pattern to match email addresses
  const emailRegex = /[\w.+-]+@[\w.+-]+\.[A-Za-z]{1,}/g;

  // Use the regular expression's exec method to find all matches in the input string
  const emailAddresses = [];
  let match;
  while ((match = emailRegex.exec(inputString)) !== null) {
    emailAddresses.push(match[0].trim());
  }
  if (unique) {
    const uniqueEmails: string[] = emailAddresses.filter((email, index, self) => {
      return index === self.findIndex((e) => e === email);
    });
    return uniqueEmails;
  }
  return emailAddresses;
};

export const sentenceToCamelCase = (text?: string) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/-/g, '')
    .split(' ')
    .reduce((s, c) => s + (c.charAt(0).toUpperCase() + c.slice(1)));
};

export const getValueUsingStringKeyPath = ({
  keyPath,
  data,
}: {
  keyPath: string;
  data: any;
}): string => {
  const keys = keyPath.split('.');

  let nestedData = data;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (nestedData && nestedData.hasOwnProperty(key)) {
      nestedData = nestedData[key];
    } else {
      nestedData = '';
      break;
    }
  }
  return nestedData;
};

export const fetchPdf = async (url: string) => {
  const res = await axios.get(url, {
    responseType: 'arraybuffer',
  });
  if (res.data) {
    return res.data;
  }
  return null;
};

export const flattenPDF = async (pdfUrl: string) => {
  try {
    const PdfBytes = await fetchPdf(pdfUrl);
    if (PdfBytes) {
      const pdfDoc = await PDFDocument.load(PdfBytes);
      pdfDoc.getPages().forEach((page) => {
        page.node.set(PDFName.of('Annots'), pdfDoc.context.obj([]));
      });
      const form = pdfDoc.getForm();

      // Flatten pdf fields
      form.flatten();
      const modifiedPdfBytes = await pdfDoc.save();
      // Download the modified PDF
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      return url;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const disablePDFFields = async (pdfUrl: string) => {
  try {
    const PdfBytes = await fetchPdf(pdfUrl);
    if (PdfBytes) {
      const pdfDoc = await PDFDocument.load(PdfBytes);
      const form = pdfDoc.getForm();
      // const pages = pdfDoc.getPages();
      // pages.forEach((page) => {
      //   const annots = page.node.Annots();
      //   if (annots) {
      //     const size = annots?.size();
      //     if (size) {
      //       for (let i = 0; i < size; i++) {
      //         annots.remove(i);
      //       }
      //     }
      //   }
      // });
      // Disable fields to read only
      form.getFields().forEach((formField) => formField.enableReadOnly());
      const modifiedPdfBytes = await pdfDoc.save();
      // Download the modified PDF
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      return url;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const removePDFFields = async (pdfUrl: string) => {
  try {
    const PdfBytes = await fetchPdf(pdfUrl);
    if (PdfBytes) {
      const pdfDoc = await PDFDocument.load(PdfBytes);
      const form = pdfDoc.getForm();
      // Remove form fields from the form
      const formFields = form.getFields();
      formFields.forEach((formField) => {
        const widget0 = formField.acroField.getWidgets()[0];
        const AP = widget0.ensureAP();
        AP.set(PDFName.of('N'), PDFRef.of(0, 0));
        form.removeField(formField);
      });
      const modifiedPdfBytes = await pdfDoc.save();
      // Download the modified PDF
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      return url;
    }

    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const removeSpecificPDFFields = async (pdfUrl: string, fieldId: string) => {
  try {
    const PdfBytes = await fetchPdf(pdfUrl);
    if (PdfBytes) {
      const pdfDoc = await PDFDocument.load(PdfBytes);
      const form = pdfDoc.getForm();
      // Remove form fields from the form
      const tmp = form.getFields().find((f) => f.getName() === fieldId);
      if (tmp) {
        const widget0 = tmp.acroField.getWidgets()[0];
        const AP = widget0.ensureAP();
        AP.set(PDFName.of('N'), PDFRef.of(0, 0));
        form.removeField(tmp);
      }
      const modifiedPdfBytes = await pdfDoc.save();
      // Download the modified PDF
      const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      return url;
    }

    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const structureTemplateNonNewFieldFromTypePDFData = ({
  template,
  details,
}: {
  template: LienTemplate | NoticeTemplate;
  details: {
    fromFieldType: FromFieldTypeReleaseTemplate | FromFieldTypeNoticeTemplate;
    dataToUse: any;
  }[];
}): PDFFieldReleaseTemplate[] => {
  try {
    if (template) {
      const fieldsToPopulate: PDFFieldReleaseTemplate[] = [];
      details.forEach(({ fromFieldType, dataToUse }) => {
        template.formFields.forEach((formField) => {
          if (formField.canPopulatePDF && formField.fromFieldType === fromFieldType && dataToUse) {
            const fieldData = (formField?.fromFieldDataKeys || [])
              .map((fromFieldDataKey, fromFieldDataKeyIndex) => {
                let value = getValueUsingStringKeyPath({
                  keyPath: fromFieldDataKey,
                  data: dataToUse,
                });
                if (!value && fromFieldDataKey.includes('subUser')) {
                  value = getValueUsingStringKeyPath({
                    keyPath: fromFieldDataKey.replaceAll('subUser', 'user'),
                    data: dataToUse,
                  });
                }
                if (
                  formField.fromFieldDataKeysDelimiters &&
                  formField.fromFieldDataKeysDelimiters[fromFieldDataKeyIndex]
                ) {
                  return value
                    ? `${value}${formField.fromFieldDataKeysDelimiters[fromFieldDataKeyIndex]}`
                    : '';
                }
                return value ? value : '';
              })
              .join(' ');

            formField.pdfFields.forEach((pdfField) => {
              if (formField.fromFieldType === 'SIGNATURE_FIELD') {
                fieldsToPopulate.push({
                  ...pdfField,
                  value: dataToUse,
                });
              } else {
                fieldsToPopulate.push({
                  ...pdfField,
                  value: fieldData || '',
                });
              }
            });
          }
        });
      });

      return fieldsToPopulate;
    }
    return [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const structureTemplateNewFieldFromTypePDFData = ({
  template,
  details,
}: {
  template?: LienTemplate | NoticeTemplate | null;
  details: { fromFieldId: string; value: any; fontSize?: number }[];
}): PDFFieldReleaseTemplate[] => {
  try {
    if (template) {
      const fieldsToPopulate: PDFFieldReleaseTemplate[] = [];
      details.forEach(({ fromFieldId, value, fontSize }) => {
        template.formFields.forEach((formField) => {
          if (formField.canPopulatePDF && formField.id === fromFieldId) {
            formField.pdfFields.forEach((pdfField) => {
              if (formField.fieldType === 'DATE') {
                let dateFormat = 'MM/DD/YYYY';
                if (formField.dateSplitFormats && formField.dateSplitFormats.length > 0) {
                  const idSplit = pdfField.id.split('Format');
                  if (idSplit.length > 1) {
                    dateFormat = formField.dateSplitFormats[parseInt(idSplit[1]) - 1];
                  }
                }
                fieldsToPopulate.push({
                  ...pdfField,
                  value: value ? moment(value).format(dateFormat) : '',
                  ...(fontSize ? { fontSize } : {}),
                });
              } else if (formField.fieldType === 'CURRENCY') {
                fieldsToPopulate.push({
                  ...pdfField,
                  value: !value && value !== 0 ? '' : formatMoney(value, 2, '.', ',', ''),
                  ...(fontSize ? { fontSize } : {}),
                });
              } else if (formField.fieldType === 'NUMBER') {
                fieldsToPopulate.push({
                  ...pdfField,
                  value: !value && value !== 0 ? '' : value,
                  ...(fontSize ? { fontSize } : {}),
                });
              } else {
                fieldsToPopulate.push({
                  ...pdfField,
                  value: value ? `${value}` : '',
                  ...(fontSize ? { fontSize } : {}),
                });
              }
            });
          }
        });
      });

      return fieldsToPopulate;
    }
    return [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const openDillToast = ({
  message,
  type,
  autoClose,
}: {
  message: string;
  type: 'SUCCESS' | 'ERROR';
  autoClose?: number;
}) => {
  if (type === 'SUCCESS') {
    toast.success(message, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
      style: {
        backgroundColor: '#ECFDF3',
        color: '#054F31',
      },
    });
  } else if (type === 'ERROR') {
    toast.error(message, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
      style: {
        backgroundColor: '#FEF0EF',
        color: '#F04438',
      },
      ...(autoClose ? { autoClose } : {}),
    });
  }
};

export const populatePdfWithFieldsData = async ({
  fieldDetails,
  url,
  releaseId = '',
}: {
  fieldDetails: PDFFieldReleaseTemplate[];
  url: string;
  releaseId?: string;
}) => {
  let existingPdfBytes = null;
  const res = await axios
    .get(url, {
      responseType: 'arraybuffer',
    })
    .catch(promiseNull);
  if (!res) {
    return null;
  }
  if (res && res.data) {
    existingPdfBytes = res.data;
  }

  if (existingPdfBytes) {
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    const page = pdfDoc.getPages()[0];
    const form = pdfDoc.getForm();
    const { width: horizontalLength, height: verticalLength } = page.getSize();
    for (const fieldDetail of fieldDetails) {
      try {
        if (fieldDetail.id.startsWith('signature')) {
          if (fieldDetail.value) {
            let signatureImage = null;
            if (fieldDetail.value.startsWith('data:image/jpeg')) {
              signatureImage = await pdfDoc.embedJpg(fieldDetail.value);
            } else {
              signatureImage = await pdfDoc.embedPng(fieldDetail.value);
            }
            const { width, height } = signatureImage.size();
            const field = form.createTextField(`dillField_text_${fieldDetail.id}`);
            field.enableMultiline();
            field.disableScrolling();
            field.setText(`E-signed using Dill ${releaseId ? `(${releaseId})` : ''}`);
            field.addToPage(page, {
              x: fieldDetail.x ? fieldDetail.x + 105 : undefined,
              y: verticalLength - (fieldDetail.y || 0),
              width: 140,
              height: fieldDetail.height || undefined,
              borderWidth: 0,
            });
            // const scaled = signatureImage.scaleToFit(width * 0.2, height * 0.08);
            const scaled = signatureImage.scaleToFit(100, 25);
            page.drawImage(signatureImage, {
              x: fieldDetail?.x ?? 377.3359375,
              y: verticalLength - (fieldDetail?.y ?? 553.203125) - 5,
              width: scaled.width,
              height: scaled.height,
            });
          }
        } else {
          const field = form.createTextField(`dillField_${fieldDetail.id}`);
          field.enableMultiline();
          field.disableScrolling();
          field.setText(`${fieldDetail?.value || ''}`);
          field.addToPage(page, {
            x: fieldDetail.x || undefined,
            y: verticalLength - (fieldDetail.y || 0),
            width: fieldDetail.width || undefined,
            height: fieldDetail.height || undefined,
            borderWidth: 0,
          });
          const da = field.acroField.getDefaultAppearance() ?? '';
          if (da) {
            const dalength = da.split(' ').length;
            const dafontsize = Number(da.split(' ')[dalength - 2]);
            const sizeOffest = 10 / dafontsize;
            const adjustfontSize = 1 / sizeOffest;

            const calculateFontSize = dafontsize - adjustfontSize;
            if (fieldDetail.fontSize) {
              const minimumFontSize = Math.min(fieldDetail.fontSize, calculateFontSize);
              field.setFontSize(minimumFontSize);
            } else {
              field.setFontSize(dafontsize - adjustfontSize);
            }
          }
        }
      } catch (error: any) {
        console.log(error);
        if (error.message.includes('SOI not found in')) {
          openDillToast({
            message: 'The image uploaded may be corrupt, please use a different signature image!',
            type: 'ERROR',
          });
        } else {
          openDillToast({ message: error.message, type: 'ERROR' });
        }
        return null;
      }
    }

    const modifiedPdfBytes = await pdfDoc.save();

    // Download the modified PDF
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    const populatedPdfUrl = URL.createObjectURL(blob);
    // setPdfUrlHistory([pdfUrlHistory[0], url]);
    return populatedPdfUrl;
  }
};

export const populatePagesPdfWithFieldsData = async ({
  fieldDetails,
  url,
  releaseId = '',
}: {
  fieldDetails: PDFFieldReleaseTemplate[];
  url: string;
  releaseId?: string;
}) => {
  let existingPdfBytes = null;
  const res = await axios
    .get(url, {
      responseType: 'arraybuffer',
    })
    .catch(promiseNull);
  if (!res) {
    return null;
  }
  if (res && res.data) {
    existingPdfBytes = res.data;
  }

  if (existingPdfBytes) {
    const groupedData: PDFFieldReleaseTemplate[][] = [];

    fieldDetails.forEach((item) => {
      const page = item.page ?? 0;
      if (!groupedData[page]) {
        groupedData[page] = [];
      }
      groupedData[page].push(item);
    });
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

    for (let index = 0; index < groupedData.length; index++) {
      const pageFields = groupedData[index];
      const pageNumber = pageFields?.[0]?.page ?? 0;
      const page = pdfDoc.getPages()[pageNumber];
      const form = pdfDoc.getForm();
      const { width: horizontalLength, height: verticalLength } = page?.getSize();
      if (pageFields && pageFields.length > 0) {
        for (const fieldDetail of pageFields) {
          try {
            if (fieldDetail.id.startsWith('signature')) {
              if (fieldDetail.value) {
                let signatureImage = null;
                if (fieldDetail.value.startsWith('data:image/jpeg')) {
                  signatureImage = await pdfDoc.embedJpg(fieldDetail.value);
                } else {
                  signatureImage = await pdfDoc.embedPng(fieldDetail.value);
                }
                const imageX = fieldDetail?.x ?? 377.3359375;
                const sigY = fieldDetail?.y ?? 553.203125;

                // PDF y grows upward, so drawImage's y is the image's BOTTOM edge and it
                // extends further UP the page from there. The old "- 5" here shifted that
                // bottom edge down past the reference line, i.e. toward whatever field
                // sits in the row below (e.g. the claimant's title) - which always renders
                // on top of this drawn image and clipped the part that dipped into it.
                // Anchoring the bottom exactly at the line removes that overlap entirely.
                const imageY = verticalLength - sigY;
                const scaled = signatureImage.scaleToFit(100, 25);
                page.drawImage(signatureImage, {
                  x: imageX,
                  y: imageY,
                  width: scaled.width,
                  height: scaled.height,
                });

                // Always split "E-signed using Dill" and "(releaseId)" onto their own
                // lines instead of trying to detect when a single line would overflow -
                // that made the box height a moving target and the last line kept landing
                // outside it. Two fixed lines is predictable to size correctly up front.
                const signatureFontSize = 8;
                const signatureLine1 = 'E-signed using Dill';
                const signatureLine2 = releaseId ? `(${releaseId})` : '';
                const signatureText = signatureLine2
                  ? `${signatureLine1}\n${signatureLine2}`
                  : signatureLine1;
                const signatureTextX = imageX + scaled.width + 8;
                const longestLineWidth = Math.max(
                  helveticaFont.widthOfTextAtSize(signatureLine1, signatureFontSize),
                  helveticaFont.widthOfTextAtSize(signatureLine2, signatureFontSize)
                );
                // Cap the field at the space actually left on the page so it never runs
                // off the edge; a single unbroken token (the release ID) can't be split
                // further, so this is a best-effort bound rather than a hard guarantee.
                const maxAvailableWidth = horizontalLength - signatureTextX - 20;
                const signatureLineHeight = signatureFontSize * 1.3;

                const field = form.createTextField(`dillField_text_${fieldDetail.id}`);
                field.enableMultiline();
                field.disableScrolling();
                field.setText(signatureText);
                field.addToPage(page, {
                  x: signatureTextX,
                  y: imageY,
                  width: Math.min(maxAvailableWidth, longestLineWidth + 6),
                  height: signatureLineHeight * (signatureLine2 ? 2 : 1) + 4,
                  borderWidth: 0,
                });
                field.setFontSize(signatureFontSize);
              }
            } else if (fieldDetail.id.startsWith('check')) {
              if (fieldDetail.value === 'X') {
                const field = form.createCheckBox(`dillField_check_${fieldDetail.id}`);
                field.addToPage(page, {
                  x: fieldDetail.x || undefined,
                  y: verticalLength - (fieldDetail.y || 0),
                  width: fieldDetail.width || undefined,
                  height: fieldDetail.height || undefined,
                  borderWidth: 0,
                });
                field.check();
              }
            } else {
              const field = form.createTextField(`dillField_${fieldDetail.id}`);
              field.enableMultiline();
              field.disableScrolling();
              field.setText(`${fieldDetail?.value || ''}`);
              field.addToPage(page, {
                x: fieldDetail.x || undefined,
                y: verticalLength - (fieldDetail.y || 0),
                width: fieldDetail.width || undefined,
                height: fieldDetail.height || undefined,
                borderWidth: 0,
              });
              const da = field.acroField.getDefaultAppearance() ?? '';
              if (da) {
                const dalength = da.split(' ').length;
                const dafontsize = Number(da.split(' ')[dalength - 2]);
                const sizeOffest = 10 / dafontsize;
                const adjustfontSize = 1 / sizeOffest;

                const calculateFontSize = dafontsize - adjustfontSize;
                if (fieldDetail.fontSize) {
                  const minimumFontSize = Math.min(fieldDetail.fontSize, calculateFontSize);
                  field.setFontSize(minimumFontSize);
                } else {
                  field.setFontSize(dafontsize - adjustfontSize);
                }
              }
            }
          } catch (error: any) {
            console.log(error);
            if (error.message.includes('SOI not found in')) {
              openDillToast({
                message:
                  'The image uploaded may be corrupt, please use a different signature image!',
                type: 'ERROR',
              });
            } else {
              openDillToast({ message: error.message, type: 'ERROR' });
            }
            return null;
          }
        }
      }
    }

    const modifiedPdfBytes = await pdfDoc.save();

    // Download the modified PDF
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    const populatedPdfUrl = URL.createObjectURL(blob);
    // setPdfUrlHistory([pdfUrlHistory[0], url]);
    return populatedPdfUrl;
  }
};

export const getSavedPastPath = () => {
  let pastPath = '';
  const pastPathList = localStorage.getItem('pastPathList');
  const timeLimitSeconds = 60 * 3;
  if (pastPathList) {
    const savedPastPathList = JSON.parse(pastPathList);
    const sortttt = savedPastPathList.sort((A: any, B: any) => {
      return new Date(B.date).getTime() - new Date(A.date).getTime();
    });
    if (sortttt.length > 0) {
      const currentTime = moment(new Date());
      const lastSavedTime = moment(new Date(sortttt[0].date));
      const diff = currentTime.diff(lastSavedTime, 'seconds');

      pastPath = sortttt[0].route;

      if (diff > timeLimitSeconds) {
        // pastPath = '';
        // localStorage.removeItem('pastPathList');
      }
    }
  }
  return pastPath;
};

export const parseReferPathParams = (referPath: string) => {
  let urlObj;
  try {
    urlObj = new URL(referPath, REACT_APP_FRONTEND_URL);
  } catch (error) {
    urlObj = new URL(referPath, window.location.href);
  }
  return urlObj.searchParams;
};

/**
 * A declined setup (`isAcceptSetup=false`) is only remembered through the saved `referPath`,
 * so a decline the user never finished outlives the session it was made in. Once the user has
 * a firebase token they have logged in/signed up, which we treat as accepting the setup, so
 * the stale decline is dropped instead of being replayed on their next visit.
 * Returns true when a stale decline was cleared.
 */
export const clearDeclinedReferPathForLoggedInUser = () => {
  if (!myFirebaseAuth.currentUser) {
    return false;
  }
  const referPath = localStorage.getItem('referPath');
  if (!referPath) {
    return false;
  }
  if (parseReferPathParams(referPath).get('isAcceptSetup') === 'true') {
    return false;
  }
  localStorage.removeItem('referPath');
  return true;
};

export const handleSavePastPath = (currentPath: string) => {
  if (!currentPath) {
    return;
  }
  const pastPathList = localStorage.getItem('pastPathList');
  let finalList: { route: string; date: string | Date }[] = [];
  if (pastPathList) {
    const savedPastPathList = JSON.parse(pastPathList);

    const check = savedPastPathList.find((item: any) => item.route === currentPath);

    if (!check) {
      finalList = [{ route: currentPath, date: new Date() }, ...savedPastPathList];
    } else {
      finalList = [
        ...savedPastPathList.map((item: any) => {
          if (item.route === currentPath) {
            return { route: item.route, date: new Date() };
          }
          return item;
        }),
      ];
    }
  }
  if (!pastPathList) {
    finalList = [{ route: currentPath, date: new Date() }];
  }
  const sortttt = finalList.sort((A: any, B: any) => {
    const dateA = moment(new Date(A.date));
    const dateB = moment(new Date(B.date));
    return dateB.diff(dateA, 'milliseconds');
  });
  localStorage.setItem('pastPathList', JSON.stringify([...sortttt.slice(0, 8)]));
};

export const removeQueryParam = ({
  path,
  paramNameToRemove,
}: {
  path: string;
  paramNameToRemove: string;
}) => {
  let urlObj;
  try {
    urlObj = new URL(path, REACT_APP_FRONTEND_URL);
  } catch (error) {
    urlObj = new URL(path, window.location.href);
  }

  const paramValue = urlObj.searchParams.get(paramNameToRemove);

  if (paramValue !== null) {
    urlObj.searchParams.delete(paramNameToRemove);
  }

  const pathAndQuery = urlObj.pathname + urlObj.search;

  return pathAndQuery;
};

// Supplier routes are nested under `/supplier/:supplierId/...`. These helpers build and
// match those paths so navigations and active-route detection work with the prefix.
export const buildSupplierPath = (
  supplierId: string | null | undefined,
  subPath: string
): string => {
  const normalizedSubPath = subPath.startsWith('/') ? subPath : `/${subPath}`;
  if (!supplierId) {
    return normalizedSubPath;
  }
  return `/supplier/${supplierId}${normalizedSubPath}`;
};

// Strips the `/supplier/:supplierId` prefix so existing `startsWith`/`split('/')` checks
// (which expect the old top-level supplier paths) keep working. Non-supplier paths are
// returned unchanged.
export const stripSupplierPrefix = (pathname: string): string => {
  const match = pathname.match(/^\/supplier\/[^/]+(\/.*)?$/);
  if (!match) {
    return pathname;
  }
  return match[1] || '/';
};

const CREDIT_APP_ROLES = ['CREDIT_APP_SALES', 'CREDIT_APP_REVIEWER', 'CREDIT_APP_DECISION_MAKER'];

export const hasCreditAppRole = (roles?: string[] | null): boolean =>
  CREDIT_APP_ROLES.some((role) => (roles ?? []).includes(role));

// Credit app users are created with SUPPLIER + SUB_SUPPLIER alongside their credit app role, but
// credit apps is the only section they can use. They land there and the other sections are
// blocked for them. An explicit AR role opts them back into AR. `DILL_ADMIN` is ignored, as
// everywhere else that decides landings.
export const isCreditAppRestricted = (roles?: string[] | null): boolean => {
  const mainRoles = (roles ?? []).filter((role) => role !== 'DILL_ADMIN');
  return (
    hasCreditAppRole(mainRoles) && !mainRoles.includes('AR_ALL') && !mainRoles.includes('AR_SALES')
  );
};

// Single source of truth for the sidebar tabs a user can use: `DashboardPage` greys out the
// disabled ones and `ProtectedRoute` blocks the routes behind them.
export const isNavItemDisabled = ({
  section,
  id,
  roles,
}: {
  section: string;
  id: string;
  roles?: string[] | null;
}): boolean => {
  const allRoles = roles ?? [];
  if (section === 'CREDIT') {
    if (allRoles.includes('DILL_ADMIN')) {
      return false;
    } else if (allRoles.includes('CREDIT_APP_SALES') && id === 'creditApplications') {
      return false;
    } else if (
      allRoles.includes('CREDIT_APP_REVIEWER') ||
      allRoles.includes('CREDIT_APP_DECISION_MAKER')
    ) {
      return false;
    }
    return true;
  }
  if (section === 'AR + ONLINE PAYMENTS') {
    if (allRoles.includes('DILL_ADMIN')) {
      return false;
    } else if (allRoles.includes('AR_SALES') && (id === 'virtualPOS' || id === 'paymentRequests')) {
      return false;
    } else if (allRoles.includes('AR_ALL')) {
      return false;
    }
    return true;
  }
  if (section === 'AI CASH APPLICATION') {
    // Part of AR rather than the credit apps workspace, everyone else keeps it.
    return isCreditAppRestricted(allRoles);
  }
  if (section === 'LIENS') {
    if (allRoles.includes('DILL_ADMIN')) {
      return false;
    } else if (allRoles.includes('AR_ALL')) {
      return false;
    } else if (isCreditAppRestricted(allRoles) || allRoles.includes('AR_SALES')) {
      return true;
    } else if (allRoles.includes('SUB_SUPPLIER')) {
      return id !== 'lienReleases';
    }
    return true;
  }
  return false;
};

// First path segment -> the nav item that owns it, so route guards can reuse `isNavItemDisabled`.
// `invoices`/`salesReceipts` no longer have a tab but their routes are still reachable.
const NAV_ITEM_BY_SEGMENT: { [segment: string]: { section: string; id: string } } = {
  dashboardCreditApplications: { section: 'CREDIT', id: 'dashboardCreditApplications' },
  creditApplications: { section: 'CREDIT', id: 'creditApplications' },
  approvalWorkflows: { section: 'CREDIT', id: 'approvalWorkflows' },
  tradeReferencesPool: { section: 'CREDIT', id: 'tradeReferencesPool' },
  creditMonitoring: { section: 'CREDIT', id: 'creditMonitoring' },
  creditApplicationRequestTemplates: {
    section: 'CREDIT',
    id: 'creditApplicationRequestTemplates',
  },
  dashboardPayments: { section: 'AR + ONLINE PAYMENTS', id: 'dashboardPayments' },
  customers: { section: 'AR + ONLINE PAYMENTS', id: 'customers' },
  payments: { section: 'AR + ONLINE PAYMENTS', id: 'payments' },
  virtualPOS: { section: 'AR + ONLINE PAYMENTS', id: 'virtualPOS' },
  paymentRequests: { section: 'AR + ONLINE PAYMENTS', id: 'paymentRequests' },
  disputes: { section: 'AR + ONLINE PAYMENTS', id: 'disputes' },
  collectionTasks: { section: 'AR + ONLINE PAYMENTS', id: 'collectionTasks' },
  promiseToPays: { section: 'AR + ONLINE PAYMENTS', id: 'promiseToPays' },
  collectionSequencesAndTriggers: {
    section: 'AR + ONLINE PAYMENTS',
    id: 'collectionSequencesAndTriggers',
  },
  invoiceDelivery: { section: 'AR + ONLINE PAYMENTS', id: 'invoiceDelivery' },
  invoices: { section: 'AR + ONLINE PAYMENTS', id: 'invoices' },
  salesReceipts: { section: 'AR + ONLINE PAYMENTS', id: 'salesReceipts' },
  cashApplication: { section: 'AI CASH APPLICATION', id: 'cashApplication' },
  supplierJobs: { section: 'LIENS', id: 'supplierJobs' },
  preliminaryNotices: { section: 'LIENS', id: 'preliminaryNotices' },
  liens: { section: 'LIENS', id: 'liens' },
  lienReleases: { section: 'LIENS', id: 'lienReleases' },
  noticeTemplates: { section: 'LIENS', id: 'noticeTemplates' },
  noticeTemplateFields: { section: 'LIENS', id: 'noticeTemplateFields' },
  lienTemplates: { section: 'LIENS', id: 'lienTemplates' },
  lienTemplateFields: { section: 'LIENS', id: 'lienTemplateFields' },
};

// Whether the tab behind a path's first segment is disabled for the user. Segments with no tab
// (profile, help, buyer routes, ...) are never blocked.
export const isNavSegmentDisabled = ({
  segment,
  roles,
}: {
  segment: string;
  roles?: string[] | null;
}): boolean => {
  const navItem = NAV_ITEM_BY_SEGMENT[segment];
  return navItem ? isNavItemDisabled({ ...navItem, roles }) : false;
};

// The nav section a path's first segment belongs to, or null when it has no tab.
export const getNavSegmentSection = (segment: string): string | null =>
  NAV_ITEM_BY_SEGMENT[segment]?.section ?? null;

// The login page resolves the supplier whose portal the user came through (`/login/:domainId`,
// `/portal/:domainId`, or a saved `/companies/.../:supplierDomainId` deep link). We keep that id
// so later redirects can tell which of the user's main buyers they actually logged in for.
const LOGIN_SUPPLIER_ID_KEY = 'loginSupplierId';

export const saveLoginSupplierId = (supplierId?: string | null) => {
  if (!supplierId) {
    return;
  }
  localStorage.setItem(LOGIN_SUPPLIER_ID_KEY, supplierId);
};

export const getLoginSupplierId = (): string => localStorage.getItem(LOGIN_SUPPLIER_ID_KEY) ?? '';

export const clearLoginSupplierId = () => localStorage.removeItem(LOGIN_SUPPLIER_ID_KEY);

// A user's main buyers come back from the API sorted by name, so picking `[0]` lands them on the
// alphabetically-first company instead of the one belonging to the supplier they logged in with.
// This moves the login supplier's main buyers to the front, keeping the name ordering within each
// group, so `[0]` is the company the user is most likely here for.
export const sortMainBuyersByLoginSupplier = <T extends { syncSupplierId?: string }>(
  mainBuyers: T[],
  loginSupplierId?: string | null
): T[] => {
  const supplierId = loginSupplierId || getLoginSupplierId();
  if (!supplierId) {
    return mainBuyers;
  }
  return [
    ...mainBuyers.filter((mainBuyer) => mainBuyer?.syncSupplierId === supplierId),
    ...mainBuyers.filter((mainBuyer) => mainBuyer?.syncSupplierId !== supplierId),
  ];
};

// Appends a branch's erpId onto its name for display, e.g. "PURVIS INDUSTRIES - WACO (12345)" —
// used anywhere a branch name is shown so the ERP branch id is visible alongside it (Branch &
// Sales Reps card, credit application tables, etc). Falls back to just the name when erpId is
// blank/unset.
export const formatBranchLabel = (branch?: {
  branchName?: string | null;
  erpId?: string | null;
}): string => {
  const branchName = branch?.branchName ?? '';
  const erpId = branch?.erpId?.trim();
  return erpId ? `${branchName} (${erpId})` : branchName;
};

export const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength - 3) + '...';
  }
  return text;
};

export function trimObject(obj: object): object {
  if (typeof obj !== 'object' || obj === null) {
    // If the value is not an object, or is null, return it as is
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(trimObject);
  }

  // Handle objects
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.trim(),
      typeof value === 'string' ? value.trim() : trimObject(value),
    ])
  );
}

export function getFirstLetters(str: string | undefined) {
  if (str) {
    return str
      .split(' ')
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toLocaleUpperCase();
  }
  return '';
}
export const convertKeysToCamelCase = (data: any[]): any[] => {
  return data.map((item) => {
    const newItem: any = {};
    for (const key in item) {
      if (item.hasOwnProperty(key) && !key.startsWith('__EMPTY')) {
        const camelCaseKey = key
          .trim()
          .replace(/\s+/g, '')
          .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) =>
            index === 0 ? letter.toLowerCase() : letter.toUpperCase()
          );
        newItem[camelCaseKey] = item[key];
      }
    }
    return newItem;
  });
};

export function removeExtraSpaces(input: string): string {
  return input.replace(/\s+/g, ' ').trim();
}

export function getFileNameExtension({
  fileName,
  isIncludePeriod = false,
}: {
  fileName: string;
  isIncludePeriod?: boolean;
}) {
  const parts = fileName.split('.');
  if (parts.length <= 1) return '';
  const ext = parts.pop();
  return ext ? (isIncludePeriod ? `.${ext.toLowerCase()}` : ext.toLowerCase()) : '';
}

export function canBuyerPaySupplier({ supplier }: { supplier: Supplier }) {
  if (supplier.isFullyVerified) {
    return true;
  } else if (
    !supplier.isFullyVerified &&
    supplier.isOptoutCardPayments &&
    supplier.dwollaCustomerStatus === 'verified'
  ) {
    return true;
  }
  return false;
}
const emailPattern = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

export const isValidEmail = (email: string) => emailPattern.test(email);

export const isValidDate = (dateStr: string) => !isNaN(Date.parse(dateStr));

export const isHTML = (str: string): boolean => /<\/?[a-z][\s\S]*>/i.test(str);

/**
 * Injects a `<base href>` tag into a raw HTML document so that any relative URLs it contains
 * (e.g. `<img src="green.gif">`) resolve against `baseHref` instead of the current page's own
 * URL. Used for third-party report HTML (e.g. Experian) that references its own image assets by
 * relative path — those assets are hosted under public/experianReferenceFiles/, and this makes
 * an <iframe srcDoc={...}> render of that HTML resolve them correctly, without having to rewrite
 * every individual `src`/`href` attribute in the document.
 */
export const injectHtmlBaseHref = (html: string, baseHref: string): string => {
  if (!html || !baseHref) return html;
  const baseTag = `<base href="${baseHref}">`;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => `${match}${baseTag}`);
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, (match) => `${match}<head>${baseTag}</head>`);
  }
  return `${baseTag}${html}`;
};

export const downloadPdfFromBuffer = (bufferObj: { data: number[] }, fileName = 'file.pdf') => {
  const uint8Array = new Uint8Array(bufferObj.data);
  const blob = new Blob([uint8Array], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Converts block-level tags (p, div) to spans so HTML pasted into a label
// renders inline instead of forcing its own line.
export const toInlineHTML = (html: string): string =>
  html.replace(/<(\/?)(p|div)(\s[^>]*)?>/gi, '<$1span$3>');

// Deterministically maps a string (e.g. a row id) to a number within [min, max].
// Used for demo-only, hardcoded columns that need to look varied/realistic per
// row without being backed by real data, while staying stable across re-renders.
export const hashStringToRange = (seed: string, min: number, max: number): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const normalized = Math.abs(hash) / 2147483647;
  return Math.floor(normalized * (max - min + 1)) + min;
};

export async function decriptValueFromEncryptedString({
  encryptedString,
  showFullValue = false,
}: {
  encryptedString: string;
  showFullValue?: boolean;
}): Promise<string> {
  try {
    if (!encryptedString) {
      return '';
    }
    const secretKey = process.env.REACT_APP_CRYPTO_KEY ?? '';
    const decrypted = CryptoJS.AES.decrypt(encryptedString, secretKey);
    const plainText = decrypted.toString(CryptoJS.enc.Utf8) ?? encryptedString;
    return showFullValue
      ? plainText.replace(/^"|"$/g, '')
      : '****' + plainText.replace(/^"|"$/g, '').slice(-4);
  } catch (error) {
    return encryptedString;
  }
}

// The supplier's ERP as named in customer-action labels ("Update customer in P21").
export const getIntegrationName = (integrationType?: SupplierIntegrationType | null) =>
  integrationType === 'QBO'
    ? 'QuickBooks Online'
    : integrationType === 'AGILITY_DMSI'
    ? 'Agility DMSi'
    : integrationType === 'INFOR_DIST_SX'
    ? 'Infor SX'
    : integrationType === 'RUTTER_NETSUITE'
    ? 'NetSuite'
    : integrationType === 'ECLIPSE' || integrationType === 'ECLIPSE_MANUAL_UPLOAD'
    ? 'Eclipse'
    : 'P21';
